import { createContext, useContext, useState, type ReactNode } from "react";

export type Lang = "en" | "pt";

const LangContext = createContext<{ lang: Lang; toggle: () => void }>({
  lang: "en",
  toggle: () => {},
});

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>("pt");
  const toggle = () => setLang((l) => (l === "en" ? "pt" : "en"));
  return (
    <LangContext.Provider value={{ lang, toggle }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}

// Helper to pick the right string
export function t(lang: Lang, en: string, pt: string) {
  return lang === "en" ? en : pt;
}

// All translations keyed by section
export const translations = {
  nav: {
    howItWorks: { en: "How it works", pt: "Como funciona" },
    pricing: { en: "Pricing", pt: "Preços" },
    requestAccess: { en: "Request access", pt: "Solicitar acesso" },
  },
  hero: {
    eyebrow: { en: "Now accepting early tenants", pt: "Aceitando primeiros clientes" },
    headline1: { en: "PIX to Lightning", pt: "PIX para Lightning" },
    headline2: { en: "One API", pt: "Uma API" },
    subtitle: {
      en: "Managed payment infrastructure for wallets and brokers. Your customers pay PIX, receive sats on Lightning. You call one endpoint.",
      pt: "Infraestrutura de pagamento gerenciada para carteiras e corretoras. Seus clientes pagam PIX e recebem sats na Lightning. Você só chama um endpoint.",
    },
    cta1: { en: "Join the waitlist", pt: "Entrar na lista de espera" },
    cta2: { en: "See how it works", pt: "Veja como funciona" },
  },
  howItWorks: {
    badge: { en: "How it works", pt: "Como funciona" },
    heading: { en: "Three steps. One integration.", pt: "Três passos. Uma integração." },
    steps: [
      {
        title: { en: "Create a deposit", pt: "Crie um depósito" },
        description: {
          en: "Your backend calls the API with an amount and a Lightning invoice. Vilverin returns a PIX payment payload.",
          pt: "Seu backend chama a API com um valor e uma invoice Lightning. Nós retornamos um payload de pagamento PIX.",
        },
      },
      {
        title: { en: "Customer pays PIX", pt: "Cliente paga via PIX" },
        description: {
          en: "Standard PIX payment — scan the QR code or copy-paste. Vilverin handles receipt, fraud checking, and conversion.",
          pt: "Pagamento PIX padrão — escaneie o QR code ou copie e cole. Cuidamos do recebimento, antifraude e conversão.",
        },
      },
      {
        title: { en: "Customer receives Lightning", pt: "Cliente recebe na Lightning" },
        description: {
          en: "Sats arrive at the customer's Lightning invoice. You get a webhook with full transaction details.",
          pt: "Sats chegam na invoice Lightning do cliente. Você recebe um webhook com todos os detalhes da transação.",
        },
      },
    ],
    codeHeading: { en: "One call. One deposit.", pt: "Uma chamada. Um depósito." },
    codeDescription: {
      en: "Create a PIX deposit with a single POST request. Receive webhooks as the payment flows through each stage.",
      pt: "Crie um depósito PIX com uma única requisição POST. Receba webhooks conforme o pagamento avança em cada etapa.",
    },
    apiBullets: {
      en: [
        "RESTful API with idempotent requests",
        "Webhook delivery with at-least-once guarantee",
        "Paginated event log for reconciliation",
        "Tenant-scoped API keys with role-based access",
      ],
      pt: [
        "API RESTful com requisições idempotentes",
        "Entrega de webhooks com garantia at-least-once",
        "Log de eventos paginado para reconciliação",
        "Chaves de API por tenant com controle de acesso",
      ],
    },
  },
  whatYouSkip: {
    badge: { en: "What you skip", pt: "O que você não precisa construir" },
    heading: { en: "Everything you don't have to build.", pt: "Tudo que você não precisa construir." },
    description: {
      en: "Each of these is weeks of engineering, compliance, and ops work. Vilverin Ramp handles all of it behind one API.",
      pt: "Cada item abaixo representa semanas de engenharia, compliance e operação. Vilverin Ramp cuida de tudo por trás de uma única API.",
    },
    items: {
      en: [
        "PSP integration and PIX merchant account",
        "Lightning node operation and channel liquidity",
        "BRL → crypto conversion pipeline",
        "Swap infrastructure and atomic settlement",
        "Wallet management and UTXO handling",
        "Fraud detection across transactions",
        "Webhook infrastructure with retry logic",
        "Transaction reconciliation and reporting",
      ],
      pt: [
        "Integração com PSP e conta de recebimento PIX",
        "Operação de node Lightning e liquidez de canais",
        "Pipeline de conversão BRL → cripto",
        "Infraestrutura de swap e liquidação atômica",
        "Gerenciamento de carteira e UTXOs",
        "Detecção de fraude entre transações",
        "Infraestrutura de webhook com retry",
        "Reconciliação e relatórios de transações",
      ],
    },
  },
  wallet: {
    badge: { en: "Non-custodial", pt: "Não-custodial" },
    heading1: { en: "Your money.", pt: "Seu dinheiro." },
    heading2: { en: "Your keys.", pt: "Suas chaves." },
    description: {
      en: "Your spread revenue from each transaction settles to your own wallet. Vilverin never holds your funds — you hold your keys, you control withdrawals.",
      pt: "Sua receita de spread de cada transação é liquidada na sua própria carteira. Vilverin nunca detém seus fundos — você controla suas chaves e seus saques.",
    },
    bullets: {
      en: [
        "Spread revenue settles to your own wallet",
        "You hold the keys — Vilverin can't spend your funds",
        "Dashboard shows balance, per-transaction revenue, and history",
        "Withdraw anytime, to anywhere",
      ],
      pt: [
        "Receita de spread liquidada na sua carteira",
        "Você detém as chaves — Vilverin não pode gastar seus fundos",
        "Dashboard mostra saldo, receita por transação e histórico",
        "Saque a qualquer momento, para qualquer lugar",
      ],
    },
    mockup: {
      label: { en: "Tenant wallet", pt: "Carteira do tenant" },
      nonCustodial: { en: "Non-custodial", pt: "Não-custodial" },
      balance: { en: "Available balance", pt: "Saldo disponível" },
      todayRevenue: { en: "Today's spread revenue", pt: "Receita de spread hoje" },
      txToday: { en: "Transactions today", pt: "Transações hoje" },
      yourSpread: { en: "Your spread", pt: "Seu spread" },
      withdraw: { en: "Withdraw →", pt: "Sacar →" },
    },
  },
  dashboard: {
    badge: { en: "Dashboard", pt: "Dashboard" },
    heading: { en: "Not just an API.", pt: "Não é só uma API." },
    description: {
      en: "Spread configuration, transaction ledger, revenue summary, and API key management. Operational tooling from day one.",
      pt: "Configuração de spread, histórico de transações, resumo de receita e gerenciamento de chaves de API. Ferramentas operacionais desde o primeiro dia.",
    },
    tabs: {
      spread: { en: "Spread", pt: "Spread" },
      transactions: { en: "Transactions", pt: "Transações" },
      revenue: { en: "Revenue", pt: "Receita" },
      wallet: { en: "Wallet", pt: "Carteira" },
      apiKeys: { en: "API Keys", pt: "Chaves API" },
    },
    spreadLabel: { en: "Your spread", pt: "Seu spread" },
    yourRevenue: { en: "your revenue", pt: "sua receita" },
    columns: {
      id: { en: "ID", pt: "ID" },
      amount: { en: "Amount", pt: "Valor" },
      satsOut: { en: "Sats out", pt: "Sats enviados" },
      vFee: { en: "Vilverin fee", pt: "Taxa Vilverin" },
      spread: { en: "Your spread", pt: "Seu spread" },
      revenue: { en: "Your revenue", pt: "Sua receita" },
      status: { en: "Status", pt: "Status" },
    },
    completed: { en: "Completed", pt: "Concluído" },
    awaitingPix: { en: "Awaiting PIX", pt: "Aguardando PIX" },
  },
  pricing: {
    badge: { en: "Pricing", pt: "Preços" },
    heading: { en: "Free to integrate. Profitable from day one.", pt: "Grátis para integrar. Lucrativo desde o primeiro dia." },
    description: {
      en: "Vilverin takes a small percentage per transaction. You set your own spread on top — that's your revenue. No monthly fees, no setup costs.",
      pt: "Vilverin cobra uma pequena porcentagem por transação. Você define seu próprio spread — essa é sua receita. Sem mensalidade, sem custos de setup.",
    },
    rateBreakdown: { en: "Rate calculator", pt: "Simulador de taxa" },
    transactionAmount: { en: "Transaction amount", pt: "Valor da transação" },
    marketRate: { en: "Market rate", pt: "Taxa de mercado" },
    vFee: { en: "Vilverin fee", pt: "Taxa Vilverin" },
    yourSpread: { en: "Your spread", pt: "Seu spread" },
    customerRate: { en: "Customer pays", pt: "Cliente paga" },
    customerReceives: { en: "Customer receives", pt: "Cliente recebe" },
    yourRevenue: { en: "Your revenue", pt: "Sua receita" },
    perTransaction: { en: "per transaction", pt: "por transação" },
    projection: { en: "At {volume} monthly volume", pt: "Com volume mensal de {volume}" },
    monthlyRevenue: { en: "your monthly revenue", pt: "sua receita mensal" },
    cards: [
      {
        title: { en: "No setup fees", pt: "Sem taxa de setup" },
        description: {
          en: "Start building for free. Pay nothing until transactions flow.",
          pt: "Comece a construir grátis. Pague apenas quando as transações fluírem.",
        },
      },
      {
        title: { en: "Your spread, your revenue", pt: "Seu spread, sua receita" },
        description: {
          en: "Configure your markup via dashboard or API. Change it anytime.",
          pt: "Configure sua margem via dashboard ou API. Altere quando quiser.",
        },
      },
      {
        title: { en: "No minimums", pt: "Sem mínimos" },
        description: {
          en: "No monthly volume requirements. Start at zero, scale linearly.",
          pt: "Sem volume mínimo mensal. Comece do zero, escale linearmente.",
        },
      },
    ],
    footer: {
      en: "Vilverin fee scales with volume.",
      pt: "A taxa Vilverin diminui com o volume.",
    },
    footerCta: { en: "Join the waitlist", pt: "Entrar na lista de espera" },
  },
  cta: {
    badge: { en: "Waitlist", pt: "Lista de espera" },
    heading: { en: "Start building with Ramp.", pt: "Comece a construir com o Ramp." },
    description: {
      en: "We're onboarding early tenants with white-glove support. Request access and tell us about your use case.",
      pt: "Estamos integrando os primeiros clientes com suporte dedicado. Solicite acesso e conte sobre seu caso de uso.",
    },
  },
  waitlistForm: {
    contactName: { en: "Contact name", pt: "Nome do contato" },
    email: { en: "Work email", pt: "E-mail corporativo" },
    company: { en: "Company name", pt: "Nome da empresa" },
    useCase: { en: "Use case (optional)", pt: "Caso de uso (opcional)" },
    submit: { en: "Join the waitlist", pt: "Entrar na lista de espera" },
    successTitle: { en: "You're on the list.", pt: "Você está na lista." },
    successDescription: {
      en: "We'll reach out when we're ready for you.",
      pt: "Entraremos em contato quando estivermos prontos.",
    },
    roleOptions: {
      en: [
        { value: "", label: "Your role" },
        { value: "cto", label: "CTO" },
        { value: "developer", label: "Developer" },
        { value: "founder", label: "Founder" },
        { value: "business", label: "Business" },
        { value: "other", label: "Other" },
      ],
      pt: [
        { value: "", label: "Seu cargo" },
        { value: "cto", label: "CTO" },
        { value: "developer", label: "Desenvolvedor" },
        { value: "founder", label: "Fundador" },
        { value: "business", label: "Negócios" },
        { value: "other", label: "Outro" },
      ],
    },
    volumeOptions: {
      en: [
        { value: "", label: "Est. monthly volume" },
        { value: "<1k", label: "< 1,000 transactions" },
        { value: "1k-10k", label: "1,000 – 10,000" },
        { value: "10k-100k", label: "10,000 – 100,000" },
        { value: "100k+", label: "100,000+" },
      ],
      pt: [
        { value: "", label: "Volume mensal estimado" },
        { value: "<1k", label: "< 1.000 transações" },
        { value: "1k-10k", label: "1.000 – 10.000" },
        { value: "10k-100k", label: "10.000 – 100.000" },
        { value: "100k+", label: "100.000+" },
      ],
    },
  },
  footer: {
    location: { en: "São Paulo, Brasil", pt: "São Paulo, Brasil" },
  },
} as const;
